import React, { useState, useEffect } from 'react';
import {
  DateRangePicker,
  createStaticRanges,
} from 'react-date-range';
import dayjs from 'dayjs';
import 'react-date-range/dist/styles.css'; // Import default styles
import 'react-date-range/dist/theme/default.css'; // Import theme styles
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { getDateRange } from 'src/utils/DateUtil';
import { selectClientType } from 'src/app/selectors/filterSelectors';
import { useSelector } from 'react-redux';

interface DateRangeFilterProps {
  onApply: (
    startDate: Date,
    endDate: Date,
    timeStamp: string,
    range: string, // Include the custom range object
  ) => void;
  timeStamp: string;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onApply,
  timeStamp,
}) => {
  const clientType = useSelector(selectClientType);
  const initialStartDate =
  clientType === 'NMS'
    ? dayjs().subtract(7, 'day').toDate()
    : dayjs().subtract(30, 'day').toDate();
  const [range, setRange] = useState([
    {
      startDate: initialStartDate,
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [checked, setChecked] = useState(timeStamp);

  const dateRangeLabel = clientType === 'NMS' ? 'Last 7 days' : 'Last 30 days';
  const [rangeLabel, setRangeLabel] = useState<string>(dateRangeLabel);

  //const [rangeLabel, setRangeLabel] = useState<string>('Last 30 days'); // Default label

  const handleSelect = (ranges: any) => {
    setRange([ranges.selection]);
    setRangeLabel(getCustomRangeLabel(ranges.selection)); // Get the label when range is selected
  };

  useEffect(() => {
    setRangeLabel(clientType === 'NMS' ? 'Last 7 days' : 'Last 30 days');
  }, [clientType]);

  const handleChange = (event) => {
    const selected = event.target.name;
    setChecked(selected);
  };

  const quickSelect = (option: string) => {
    const { startDate, endDate } = getDateRange(option);
    setRange([
      {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        key: 'selection',
      },
    ]);
    setRangeLabel(option); // Set the label when quick select is used
  };

  const predefinedRanges = [
    
    {
      label: 'Last 7 days',
      range: () => ({
        startDate: dayjs().subtract(7, 'day').toDate(),
        endDate: new Date(),
      }),
    },
    {
      label: 'Last 30 days',
      range: () => ({
        startDate: dayjs().subtract(30, 'day').toDate(),
        endDate: new Date(),
      }),
    },
    
    {
      label: 'This month',
      range: () => ({
        startDate: dayjs().startOf('month').toDate(),
        endDate: dayjs().endOf('month').toDate(),
      }),
    },
    
    {
      label: 'This week',
      range: () => ({
        startDate: dayjs().startOf('week').toDate(),
        endDate: dayjs().endOf('week').toDate(),
      }),
    },
    {
      label: 'This year',
      range: () => ({
        startDate: dayjs().startOf('year').toDate(),
        endDate: dayjs().endOf('year').toDate(),
      }),
    },
    {
      label: 'Last year',
      range: () => ({
        startDate: dayjs().subtract(1, 'year').startOf('year').toDate(),
        endDate: dayjs().subtract(1, 'year').endOf('year').toDate(),
      }),
    },
    
    
  ];
  const getCustomRangeLabel = (selectedRange: any): string => {
    // Compare selected range with predefined ranges
    const match = predefinedRanges.find((range) => {
      const predefinedRange = range.range(); // Call the range function to get startDate and endDate
      return (
        dayjs(selectedRange.startDate).isSame(predefinedRange.startDate, 'day') &&
        dayjs(selectedRange.endDate).isSame(predefinedRange.endDate, 'day')
      );
    });
    if (!match) {
      return `${dayjs(selectedRange.startDate).format('YYYY-MM-DD')} - ${dayjs(
        selectedRange.endDate
      ).format('YYYY-MM-DD')}`;
    }
    return match.label
  };
  

  const customStaticRanges = createStaticRanges(predefinedRanges);

  return (
    <Box sx={{ p: 2, minWidth: 350 }}>
      {/* Quick Options */}

      {/* Date Range Picker */}
      <DateRangePicker
        ranges={range}
        onChange={handleSelect}
        dateDisplayFormat="yyyy-MM-dd"
        editableDateInputs={true}
        showMonthAndYearPickers={false}
        months={2} // Display two months side by side
        direction="horizontal"
        rangeColors={['#ff6d0f']}
        staticRanges={[...customStaticRanges]}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked === 'storeLocal'}
                onChange={handleChange}
                color="primary"
                name="storeLocal"
              />
            }
            label="Storelocal"
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleChange}
                checked={checked === 'UTC'}
                name="UTC"
                color="primary"
              />
            }
            label="UTC"
          />
        </FormGroup>
        <Button
          onClick={() =>
            onApply(range[0].startDate, range[0].endDate, checked, rangeLabel)
          }
          variant="contained"
          color="primary"
        >
          Apply ({rangeLabel})
        </Button>
      </Box>
    </Box>
  );
};

export default DateRangeFilter;
